import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Grid } from "@/comn/components";
import { utils, comnUtils, comnEnvs } from "@/comn/utils"; // 프로젝트 공통 유틸
import { Page, Group, Layout, Button, FormControl } from "@/comn/components"; // 화면 구성 컴포넌트
import { useFetch, useForm, useGrid, useStore, useToast, useResource, useModal, usePage, usePopup } from "@/comn/hooks";
import { APIS, SF_USR_APLY_INFO, URLS } from "@/tra/tancis/ptli/usr/bidraprv/services/PtliBidrAprvService";

import { getNameOfJSDocTypedef } from "typescript";

/*
 * Bidder Approval Request Details
 * !== 공매자 승인 요청 상세 ==!
 */
export const PTLI0920002S = (): JSX.Element => {
    const { pgeUid, menuId } = usePage();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const modal = useModal(); // Modal Window Hook !== Modal 창 Hook ==!
    const { openPopup } = usePopup();
    const { pgeStore, setStore, getStore } = useStore({ pgeUid: pgeUid }); // Page Store Hook !== 화면 데이터 저장 Hook ==!
    const toast = useToast();
    const { xtrnUsrAplyId } = useParams(); // Key Information of this Component Router !== 라우터에 정의된 키정보 ==!
    const [rtrnYn, setRtrnYn] = useState("N");
    /*
     * Declaration Hook, Meta
     * !== Hook, 메타 정보 정의 ==!
     */
    useResource({
        defaultSchema: [
            { area: "comnCd", comnCd: "COM0011" },
            { area: "comnCd", comnCd: "CGM0095" },
            { area: "comnCd", comnCd: "CGM0096" },
            { area: "cstmCd" },
            { area: "cntyCd" },
        ],
    });

    /*
     * Declaration Form
     * !== 폼 정의 ==!
     */
    const form = {
        // User Information !== 사용자(대표자) 정보 ==!
        usrAplyInfo: useForm({
            defaultSchema: SF_USR_APLY_INFO,
        }),
    };

    const grid = {};

    /*
     * Declaration Fetch
     * !== Fetch 정의 ==!
     */
    const fetch = {
        // Get License User Information !== 면허 사용자 정보 조회 ==!
        getBidrAprvRqstInfo: useFetch({
            api: () => APIS.getBidrAprvRqstInfo(xtrnUsrAplyId),
            enabled: !!xtrnUsrAplyId,
            onSuccess: (data) => {
                const bidrAprvRqstInfo = data.bidrAprvRqstInfo.content;

                form.usrAplyInfo.setValues(bidrAprvRqstInfo);

                form.usrAplyInfo.setEditable(
                    "rtrnRsn",
                    bidrAprvRqstInfo.aprvYn == "N" && bidrAprvRqstInfo.rtrnRsn == null,
                );

                setRtrnYn(bidrAprvRqstInfo.rtrnRsn == null ? "N" : "Y");
            },
            onError: (error) => {},
            showToast: true,
        }),
        // Execute Bidder Approval !== 공매자 승인 ==!
        executeBidrAprv: useFetch({
            api: (data) => APIS.executeBidrAprv({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00066", // Successfully approved., !== 정상적으로 승인되었습니다. ==!
                });
                fetch.getBidrAprvRqstInfo.fetch();
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.usrAplyInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
        // Execute Bidder Return !== 공매자 반려 ==!
        executeBidrRtrn: useFetch({
            api: (data) => APIS.executeBidrRtrn({ ...data }),
            onSuccess: () => {
                modal.openModal({
                    content: "msg.com.00065", // Successfully returned., !== 정상적으로 반려되었습니다. ==!
                });
                fetch.getBidrAprvRqstInfo.fetch();
            },
            onError: (error) => {
                console.log(error);
                const data = error.response.data;
                if (error.response.status === 400) {
                    form.usrAplyInfo.setErrors(error.response.data.errors);
                }

                modal.openModal({
                    content: data.errorMessage,
                });
            },
        }),
    };

    const render = {
        coTpList: {
            cell: {
                cstmOfceCdd: (props: any) => {
                    const { binding, rowValues, value } = props;

                    const cstmOfceNm = utils.getCodeLabel("cstmCd", value);

                    return `${value}, ${cstmOfceNm}`;
                },
            },
        },
    };

    /*
     * Declaration Event Handler
     * !== 이벤트 핸들러 정의 ==!
     */
    const handler = {
        // Execute Bidder Approval !== 공매자 승인 ==!
        executeBidrAprv: () => {
            if (!comnUtils.isEmpty(form.usrAplyInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.usrAplyInfo.handleSubmit(
                () => {
                    const data = form.usrAplyInfo.getValues();
                    modal.openModal({
                        content: "msg.com.00053", // Confirm approve? !== 승인하시겠습니까? ==!
                        onConfirm: () => {
                            fetch.executeBidrAprv.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
        // Execute Bidder Return !== 공매자 반려 ==!
        executeCoRtrn: () => {
            if (comnUtils.isEmpty(form.usrAplyInfo.getValue("rtrnRsn"))) {
                form.usrAplyInfo.setError("rtrnRsn", {
                    message: "msg.ptl.00737",
                    type: "error",
                });
            } else {
                form.usrAplyInfo.clearErrors("rtrnRsn");
            }

            if (!comnUtils.isEmpty(form.usrAplyInfo.errors)) {
                toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                return;
            }
            form.usrAplyInfo.handleSubmit(
                () => {
                    const data = form.usrAplyInfo.getValues();
                    modal.openModal({
                        content: "msg.com.00052", // Confirm return? !== 반려하시겠습니까? ==!
                        onConfirm: () => {
                            fetch.executeBidrRtrn.fetch(data);
                        },
                    });
                },
                () => {
                    toast.showToast({ type: "warning", content: "msg.00002" }); // Data is invalid, !== 데이터가 유효하지않습니다. ==!
                },
            )();
        },
    };

    useEffect(() => {
        fetch.getBidrAprvRqstInfo.fetch();
        // fetch.getCoTpAplyList.fetch();
        form.usrAplyInfo.setEditable(false);
    }, []);

    const aprvYn = form.usrAplyInfo.watch("aprvYn");
    const rtrnRsn = form.usrAplyInfo.watch("rtrnRsn");
    return (
        <Page>
            <form>
                <Group>
                    <Group.Header>
                        <Group.Title title={t("T_USR_INFO")} />
                    </Group.Header>
                    <Group.Body>
                        <Group.Section>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.xtrnUsrIdntfNo} />
                                <Group.Control {...form.usrAplyInfo.schema.usrNm}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.frgnrYn}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.usrMoblNo}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.usrEmlAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row>
                                <Group.Control {...form.usrAplyInfo.schema.rsdtAddr}></Group.Control>
                            </Group.Row>
                            <Group.Row hidden={aprvYn == "Y"}>
                                <Group.Control
                                    {...form.usrAplyInfo.schema.rtrnRsn}
                                    onChange={() => {
                                        if (!comnUtils.isEmpty(form.usrAplyInfo.getValue("rtrnRsn"))) {
                                            form.usrAplyInfo.clearErrors("rtrnRsn");
                                        }
                                    }}
                                ></Group.Control>
                            </Group.Row>
                        </Group.Section>
                        <Layout direction="row">
                            <Layout.Left>
                                <Button
                                    role="list"
                                    onClick={() => {
                                        navigate(URLS.ptli0920001q);
                                    }}
                                ></Button>
                            </Layout.Left>
                            <Layout.Right hidden={aprvYn == "Y" || rtrnYn == "Y"}>
                                <Layout.Button
                                    color="blue"
                                    onClick={() => {
                                        handler.executeBidrAprv();
                                    }}
                                >
                                    {t("B_APRV")}
                                </Layout.Button>
                                <Layout.Button
                                    color="error"
                                    onClick={() => {
                                        handler.executeCoRtrn();
                                    }}
                                >
                                    {t("B_RTRN")}
                                </Layout.Button>
                            </Layout.Right>
                        </Layout>
                    </Group.Body>
                </Group>
            </form>
        </Page>
    );
};
